<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title || 'Madam size calculator' }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text v-if="chart" style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.sectionName" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <label class="label-input">Content</label>
          <div class="group-item">
            <div>
              <label>Title</label>
              <v-text-field v-model="fields.title" hide-details class="mb-3" />
            </div>
            <div>
              <label>Text when nothing selected</label>
              <v-text-field v-model="chart.emptyText" hide-details class="mb-3" />
            </div>
            <div>
              <label>Fits text</label>
              <v-text-field
                v-model="chart.fitsText"
                hint="Use {size} to insert the size, e.g. Size {size} fits:"
                persistent-hint
                class="mb-3"
              />
            </div>
            <label class="d-block mb-2">Icon</label>
            <UploadImage
              @callback="obj => (fields.iconPrefix = obj.imageSrc || '')"
              :image="{ imageSrc: fields.iconPrefix || '' }"
              :height="`100`"
              :typeUpload="'image'"
              @callbackDelete="() => (fields.iconPrefix = '')"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Band</label>
          <div class="group-item">
            <div>
              <label>Label (placeholder)</label>
              <v-text-field v-model="chart.band.label" hide-details class="mb-3" />
            </div>
            <div class="d-flex justify-space-between align-center">
              <label class="mb-0">Options ({{ chart.band.options.length }})</label>
              <v-btn small text color="primary" @click="handleAddBand">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add option</span>
              </v-btn>
            </div>
            <v-row dense class="mt-1">
              <v-col v-for="(_, index) in chart.band.options" :key="`band-${index}`" cols="6" md="3">
                <v-text-field
                  v-model="chart.band.options[index]"
                  dense
                  outlined
                  hide-details
                  append-icon="mdi-delete"
                  @click:append="handleRemoveBand(index)"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Cup</label>
          <div class="group-item">
            <div>
              <label>Label (placeholder)</label>
              <v-text-field v-model="chart.cup.label" hide-details class="mb-3" />
            </div>
            <div class="d-flex justify-space-between align-center">
              <label class="mb-0">Options ({{ chart.cup.options.length }})</label>
              <v-btn small text color="primary" @click="handleAddCup">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add option</span>
              </v-btn>
            </div>
            <v-row v-for="(item, index) in chart.cup.options" :key="`cup-${index}`" dense class="mt-1">
              <v-col cols="5">
                <v-text-field v-model="item.value" label="Value" dense outlined hide-details />
              </v-col>
              <v-col cols="5">
                <v-text-field v-model="item.label" label="Label" dense outlined hide-details />
              </v-col>
              <v-col cols="2" class="d-flex align-center">
                <v-btn icon @click="handleRemoveCup(index)">
                  <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Result sizes ({{ chart.result.sizes.length }})</label>
            <v-btn small text color="primary" @click="handleAddSize">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add size</span>
            </v-btn>
          </div>
          <div class="group-item mt-2">
            <label>Label (placeholder)</label>
            <v-text-field v-model="chart.result.label" hide-details />
          </div>
          <v-expansion-panels accordion class="mt-2">
            <v-expansion-panel v-for="(size, index) in chart.result.sizes" :key="`size-${index}`">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center">
                  <div>{{ size.display || '(empty)' }} — {{ size.fits.length }} fits</div>
                  <v-btn icon @click.stop="handleRemoveSize(index)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div>
                  <label>
                    Size display
                  </label>
                  <v-text-field v-model="size.display" hide-details class="mb-3" />
                </div>
                <div>
                  <label>Fits (band + cup)</label>
                  <v-select
                    v-model="size.fits"
                    :items="getFitItems(index)"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    hide-details
                    hint="Each combination can only belong to one size."
                    persistent-hint
                  />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { updateHtmlFromData } from '../common/html/update.js';
import { defaultSizeChart } from '@/section-templates/hurrybox/html-snippets/size-calculator.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const clone = obj => JSON.parse(JSON.stringify(obj));

export default {
  name: 'madam-size-calculator-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chart: null,
      fields: {
        sectionName: 'Madam size calculator',
        title: '',
        enableAnchor: false,
        iconPrefix: '',
        settings: {
          paddingTop: false,
          paddingBottom: false,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
      },
    };
  },
  computed: {
    allCombos() {
      if (!this.chart) return [];
      const combos = [];
      this.chart.band.options.forEach(band => {
        this.chart.cup.options.forEach(cup => {
          if (band === '' || cup.value === '') return;
          const combo = `${band}${cup.value}`;
          if (!combos.includes(combo)) combos.push(combo);
        });
      });
      return combos;
    },
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      const { sizeChart, ...rest } = extract;
      this.fields = {
        ...this.fields,
        ...rest,
        sectionName: this.sectionEditor.section?.title || this.fields.sectionName,
        settings: { ...this.fields.settings, ...(rest.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(rest.containerPage || {}) },
      };
      this.chart = clone(sizeChart || defaultSizeChart);
    },
    // combos already taken by other sizes are disabled, so a combo belongs to one size only
    getFitItems(sizeIndex) {
      const usedByOthers = new Set();
      this.chart.result.sizes.forEach((size, i) => {
        if (i !== sizeIndex) size.fits.forEach(fit => usedByOthers.add(fit));
      });
      return this.allCombos.map(combo => ({ text: combo, value: combo, disabled: usedByOthers.has(combo) }));
    },
    handleAddBand() {
      this.chart.band.options.push('');
    },
    handleRemoveBand(index) {
      this.chart.band.options.splice(index, 1);
    },
    handleAddCup() {
      this.chart.cup.options.push({ value: '', label: '' });
    },
    handleRemoveCup(index) {
      this.chart.cup.options.splice(index, 1);
    },
    handleAddSize() {
      this.chart.result.sizes.push({ display: '', fits: [] });
    },
    handleRemoveSize(index) {
      this.chart.result.sizes.splice(index, 1);
    },
    buildCleanChart() {
      const chart = clone(this.chart);
      const bands = [];
      chart.band.options.forEach(v => {
        const value = String(v).trim();
        if (value && !bands.includes(value)) bands.push(value);
      });
      chart.band.options = bands;

      const cups = [];
      chart.cup.options.forEach(o => {
        const value = String(o.value).trim();
        if (value && !cups.some(c => c.value === value)) cups.push({ value, label: String(o.label).trim() || value });
      });
      chart.cup.options = cups;

      // drop combos that no longer exist and combos already taken by an earlier size
      const valid = new Set();
      bands.forEach(b => cups.forEach(c => valid.add(`${b}${c.value}`)));
      const taken = new Set();
      chart.result.sizes = chart.result.sizes
        .filter(size => String(size.display).trim())
        .map(size => ({
          display: String(size.display).trim(),
          fits: size.fits.filter(fit => {
            if (!valid.has(fit) || taken.has(fit)) return false;
            taken.add(fit);
            return true;
          }),
        }));
      return chart;
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const { sectionName, ...data } = this.fields;
      const updateHtml = updateHtmlFromData.sectionMadamSizeCalculator(this.sectionEditor.section.html, {
        ...data,
        sizeChart: this.buildCleanChart(),
      });
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: sectionName,
        html: updateHtml,
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
